// SwapPicker — staff picks a teammate to cover their shift (Sophi App v2 swap).
//
// Triggered from the "Encontré quien me cubra" branch of EventDetailModal's
// "No puedo ir" chooser. Lists compatible-role, app-using, available staff;
// staff picks one, server creates a pending swap request, target gets a DM
// with accept/reject. Once the target accepts, Operaciones (Angie/Wendy)
// gives the final approval before the swap executes (since 2026-07-20).
// No penalty regardless of timing.
//
// If staff already has a pending swap on this event, the picker shows the
// pending state + a "Cancelar petición" button instead.

import { useEffect, useState, useMemo } from 'preact/hooks';
import {
  getSwapCandidates, requestSwap, withdrawSwap,
  ApiError,
  type SwapCandidate,
} from '../lib/api';
import { haptic } from '../lib/telegram';

interface SwapPickerProps {
  eventId: string;
  eventTitle?: string;
  /** If passed, the screen opens directly in pending-state mode (showing withdraw UI). */
  initialPendingReqId?: string;
  initialPendingToName?: string;
  initialPendingExpiresAt?: string;
  onClose: () => void;
}

type Stage = 'loading' | 'ready' | 'submitting' | 'pending' | 'done' | 'error';

export function SwapPicker({
  eventId, eventTitle, initialPendingReqId, initialPendingToName, initialPendingExpiresAt, onClose,
}: SwapPickerProps) {
  const [stage, setStage] = useState<Stage>(initialPendingReqId ? 'pending' : 'loading');
  const [sourceRol, setSourceRol] = useState('');
  const [candidates, setCandidates] = useState<SwapCandidate[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingReqId, setPendingReqId] = useState<string | null>(initialPendingReqId || null);
  const [pendingToName, setPendingToName] = useState<string>(initialPendingToName || '');
  const [pendingExpiresAt, setPendingExpiresAt] = useState<string>(initialPendingExpiresAt || '');
  const [withdrawBusy, setWithdrawBusy] = useState(false);

  useEffect(() => {
    if (stage !== 'loading') return;
    (async () => {
      try {
        const r = await getSwapCandidates(eventId);
        setSourceRol(r.sourceRol);
        setCandidates(r.candidates);
        setStage('ready');
      } catch (e) {
        const msg = e instanceof ApiError && e.body && (e.body as any).error
          ? (e.body as any).error
          : (e as Error).message;
        setError(msg);
        setStage('error');
      }
    })();
  }, [eventId, stage]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.nombre.toLowerCase().includes(q));
  }, [candidates, query]);

  async function pick(c: SwapCandidate) {
    if (stage === 'submitting') return;
    setStage('submitting');
    setError(null);
    haptic('light');
    try {
      const r = await requestSwap(eventId, c.staffId);
      setPendingReqId(r.reqId);
      setPendingToName(r.toName);
      setPendingExpiresAt(r.expiresAt);
      setStage('pending');
      haptic('success');
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : null;
      const errMsg = apiErr?.message || (e as Error).message;
      // Existing pending → server returns 409 with reqId so we can show pending state.
      if (apiErr && apiErr.status === 409 && apiErr.body && (apiErr.body as any).reqId) {
        setPendingReqId((apiErr.body as any).reqId);
        setPendingToName(c.nombre);
        setStage('pending');
        return;
      }
      setError(translateError(errMsg));
      setStage('ready');
      haptic('error');
    }
  }

  async function onWithdraw() {
    if (!pendingReqId || withdrawBusy) return;
    if (!confirm('¿Cancelar la solicitud de cambio?')) return;
    setWithdrawBusy(true);
    haptic('warning');
    try {
      await withdrawSwap(pendingReqId);
      setStage('done');
      // Show brief confirmation then close.
      setTimeout(() => onClose(), 1000);
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : null;
      if (apiErr && apiErr.status === 409) {
        const cur = apiErr.body && (apiErr.body as any).currentStatus;
        setError(
          cur === 'accepted' ? `Ya no se puede cancelar: ${pendingToName} aceptó el cambio.`
          : cur === 'rejected' ? 'Ya no se puede cancelar: tu petición fue rechazada.'
          : 'Esta petición ya no está pendiente.'
        );
      } else {
        setError((e as Error).message);
      }
      haptic('error');
    } finally {
      setWithdrawBusy(false);
    }
  }

  return (
    <div class="event-chat-screen">
      <header class="event-chat-header">
        <button class="event-chat-back" onClick={onClose} aria-label="Cerrar">←</button>
        <div class="event-chat-titles">
          <div class="event-chat-title">Cambio de turno · {eventTitle || eventId}</div>
          <div class="event-chat-sub">Sin penalidad si alguien acepta</div>
        </div>
      </header>

      <div class="mireporte-body">
        {stage === 'loading' && <div class="loading"><span class="spinner" /></div>}

        {stage === 'error' && (
          <>
            <div class="mireporte-error">⚠️ {translateError(error || 'No pudimos cargar.')}</div>
            <button class="btn primary" onClick={onClose}>Volver</button>
          </>
        )}

        {stage === 'pending' && pendingReqId && (
          <div class="swap-pending">
            <div class="swap-pending-icon">⏳</div>
            <h3>Esperando respuesta{pendingToName ? ` de ${pendingToName}` : ''}</h3>
            <p class="mireporte-meta">
              Le enviamos un mensaje. Si acepta, Operaciones da la aprobación final y el cambio queda sin penalidad.
              {pendingExpiresAt && <><br /><br />La solicitud expira <strong>{formatRelativeTo(pendingExpiresAt)}</strong>.</>}
            </p>
            {error && <div class="mireporte-error">⚠️ {error}</div>}
            <button class="btn danger-soft" onClick={onWithdraw} disabled={withdrawBusy}>
              {withdrawBusy ? 'Cancelando…' : 'Cancelar petición'}
            </button>
          </div>
        )}

        {stage === 'done' && (
          <div class="mireporte-success">
            <div class="mireporte-success-icon">✓</div>
            <h3>Petición cancelada</h3>
          </div>
        )}

        {(stage === 'ready' || stage === 'submitting') && !pendingReqId && (
          <>
            <h3>Elige quién cubrirá tu turno</h3>
            <p class="mireporte-meta">
              Tu rol: <strong>{sourceRol || '—'}</strong>. Solo aparecen compañeros certificados
              para ese rol, con la app activa y sin otro evento ese día.
            </p>

            <input
              type="search"
              class="swap-search"
              placeholder="Buscar por nombre…"
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            />

            {error && <div class="mireporte-error">⚠️ {error}</div>}

            {filtered.length === 0 && candidates.length === 0 && (
              <div class="mireporte-blocked">
                <div class="mireporte-blocked-icon">🙅</div>
                <h3>Nadie disponible</h3>
                <p class="mireporte-meta">
                  No hay compañeros certificados para tu rol disponibles ese día. Usa "Necesito ayuda" para que Sophi busque reemplazo.
                </p>
              </div>
            )}

            {filtered.length === 0 && candidates.length > 0 && (
              <p class="mireporte-meta">Sin coincidencias para "{query}".</p>
            )}

            {filtered.length > 0 && (
              <ul class="swap-candidate-list">
                {filtered.map((c) => (
                  <li key={c.staffId} class="swap-candidate-row">
                    <div class="swap-candidate-info">
                      <div class="swap-candidate-name">
                        {c.nombre}
                        {c.recomendado && <span class="badge-role badge-recommend" style={{ marginLeft: 6 }}>⭐ Recomendado</span>}
                        {c.esCapitan && <span class="badge-role badge-captain" style={{ marginLeft: 6 }}>👑</span>}
                      </div>
                      <div class="swap-candidate-meta">
                        {c.nivelSenioridad && <span>{c.nivelSenioridad}</span>}
                        {c.tipoEmpleo && <span>· {c.tipoEmpleo}</span>}
                      </div>
                    </div>
                    <button
                      class="btn primary swap-pick-btn"
                      disabled={stage === 'submitting'}
                      onClick={() => pick(c)}
                    >
                      Pedirle
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function translateError(code: string): string {
  switch (code) {
    case 'event_not_found': return 'No encontramos el evento.';
    case 'source_not_assigned': return 'Ya no estás asignado a este evento.';
    case 'event_closed': return 'Este evento ya está cerrado.';
    case 'target_not_eligible': return 'Esa persona ya no es elegible — refresca la lista.';
    case 'swap_already_pending': return 'Ya tienes una solicitud pendiente para este evento.';
    case 'bridge_unavailable': return 'Servicio temporalmente fuera. Reintenta en un minuto.';
    case 'event_started': return 'Este evento ya empezó — los cambios de turno se piden antes de la hora de llegada. Si no puedes ir, habla con Angie o Wendy.';
    case 'event_postponed': return 'Este evento está pospuesto — Operaciones avisará la nueva fecha. Si no vas a poder, habla con Angie o Wendy.';
    default: return code;
  }
}

function formatRelativeTo(iso: string): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diffMs = t - Date.now();
  if (diffMs <= 0) return 'ahora';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `en ${mins} min`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins ? `en ${hours}h ${remMins}min` : `en ${hours}h`;
}
